import React, { useMemo, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'

import { MESSAGE, ROUTE, CONTENT_TYPE } from '../config'
import { sortContentsByTitle, isPdf } from '../extras/utils'
import { trackComponentManualFolderClick } from 'tw-oi-core/services/analytics'
import { isOffline } from '../services/offlineDetect'
import {
  downloadComponentManualPdf,
  getOfflineComponentManualPdfKeys,
  getIndividualComponentManualPdfKeys,
  getOfflineVisibleComponentManualPdfs,
  removeComponentManualPdf,
} from '../services/offlineComponentManualPdf'

import { connect } from 'react-redux'
import BrowseGuideLayout from '../components/BrowseGuideLayout'
import ContentList from '../components/ContentList'
import { browseListIcon } from '../components/browseListIcon'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import Loader from '../components/Loader'
import { filteredContents } from './BrandSpecificInformation/BrandSpecificInformation'

import '../styles/Topics.scss'

const pdfListIcon = browseListIcon(PictureAsPdfOutlinedIcon, 'icon-list-pdf')

function mapPdfPublication(pub, link) {
  return {
    ...pub,
    link,
    type: CONTENT_TYPE.TOPIC,
    isComponentManualPdf: true,
    pdfUrl: pub.bodyPdf?.url,
    pdfTags: pub.tags || [],
    icon: pdfListIcon,
  }
}

const ComponentManualsContent = ({
  baseRoute,
  publications,
  match,
  currentId,
  contents,
  vehicleInfo,
  offlineVehicleId,
  refreshOfflineCategories,
}) => {
  const activeVehicleId = offlineVehicleId || currentId
  const [downloadedPdfKeys, setDownloadedPdfKeys] = useState(new Set())
  const [individualPdfKeys, setIndividualPdfKeys] = useState(new Set())
  const [downloadingPdfKeys, setDownloadingPdfKeys] = useState(new Set())
  const [offlinePdfState, setOfflinePdfState] = useState({
    ready: false,
    pdfs: [],
  })
  const offline = isOffline()

  const pdfs = useMemo(() => (publications || []).filter(isPdf), [publications])

  const refreshDownloadedPdfs = useCallback(async() => {
    if (!activeVehicleId) {
      setDownloadedPdfKeys(new Set())
      setIndividualPdfKeys(new Set())
      setOfflinePdfState({ ready: true, pdfs: [] })
      return
    }

    if (offline) {
      const { keys, pdfs: visiblePdfs } = await getOfflineVisibleComponentManualPdfs(
        activeVehicleId,
        publications || []
      )
      const individualKeys = await getIndividualComponentManualPdfKeys(activeVehicleId)
      setDownloadedPdfKeys(keys)
      setIndividualPdfKeys(individualKeys)
      setOfflinePdfState({ ready: true, pdfs: visiblePdfs })
      return
    }

    const [keys, individualKeys] = await Promise.all([
      getOfflineComponentManualPdfKeys(activeVehicleId, pdfs),
      getIndividualComponentManualPdfKeys(activeVehicleId),
    ])
    setDownloadedPdfKeys(keys)
    setIndividualPdfKeys(individualKeys)
    setOfflinePdfState({ ready: true, pdfs: [] })
  }, [activeVehicleId, pdfs, publications, offline])

  useEffect(() => {
    refreshDownloadedPdfs()
  }, [refreshDownloadedPdfs])

  const handleDownloadPdf = useCallback(async(topic) => {
    if (!activeVehicleId || !topic?.resourceKey) return

    setDownloadingPdfKeys(prev => new Set(prev).add(topic.resourceKey))

    try {
      await downloadComponentManualPdf(activeVehicleId, topic, {
        contents,
        publications: pdfs,
        vehicleInfo,
      })
      await refreshDownloadedPdfs()
      if (refreshOfflineCategories) await refreshOfflineCategories()
    } finally {
      setDownloadingPdfKeys(prev => {
        const next = new Set(prev)
        next.delete(topic.resourceKey)
        return next
      })
    }
  }, [activeVehicleId, contents, pdfs, refreshDownloadedPdfs, refreshOfflineCategories, vehicleInfo])

  const handleRemovePdf = useCallback(async(topic) => {
    if (!activeVehicleId || !topic?.resourceKey) return

    await removeComponentManualPdf(activeVehicleId, topic.resourceKey, topic.pdfUrl)
    await refreshDownloadedPdfs()
    if (refreshOfflineCategories) await refreshOfflineCategories()
  }, [activeVehicleId, refreshDownloadedPdfs, refreshOfflineCategories])

  const [publicationsWithinTag, foldersAsTags] = useMemo(() => {
    const visiblePdfs = offline
      ? (offlinePdfState.ready ? offlinePdfState.pdfs : [])
      : pdfs

    if (match.params.pseudoFolderName) {
      const pubsWithoutTags = sortContentsByTitle(
        visiblePdfs
          .filter(pub => pub.tags && pub.tags.includes(match.params.pseudoFolderName))
          .map(pub => mapPdfPublication(
            pub,
            `${baseRoute}${ROUTE.BROWSE}${ROUTE.PDFS}/${match.params.pseudoFolderName}${ROUTE.CONTENT}/${pub.resourceKey}`
          ))
      )

      return [pubsWithoutTags, []]
    }

    const pubsWithoutTags = visiblePdfs
      .filter(pub => !pub.tags || !pub.tags.length)
      .map(pub => mapPdfPublication(
        pub,
        `${baseRoute}${ROUTE.BROWSE}${ROUTE.PDFS}${ROUTE.CONTENT}/${pub.resourceKey}`
      ))
      .sort((a, b) => a.title.localeCompare(b.title))

    const tagTitles = offline
      ? [...new Set(visiblePdfs.reduce((tags, pub) => tags.concat(pub.tags || []), []))].sort()
      : [...new Set(pdfs.reduce((tags, pub) => tags.concat(pub.tags || []), []))].sort()

    const folders = tagTitles.map(tagTitle => ({
      title: tagTitle,
      type: CONTENT_TYPE.FOLDER,
      resourceKey: encodeURIComponent(tagTitle),
      link: `${baseRoute}${ROUTE.BROWSE}${ROUTE.PDFS}/${tagTitle}`,
      icon: browseListIcon(FolderOutlinedIcon),
    }))

    return [pubsWithoutTags, folders]
  }, [pdfs, match, baseRoute, offline, offlinePdfState])

  const pdfListProps = {
    downloadedPdfResourceKeys: downloadedPdfKeys,
    individuallyDownloadedPdfResourceKeys: individualPdfKeys,
    downloadingPdfResourceKeys: downloadingPdfKeys,
    onDownloadComponentManualPdf: handleDownloadPdf,
    onRemoveComponentManualPdf: handleRemovePdf,
  }

  if (offline && !offlinePdfState.ready) {
    return <Loader className='inverse' />
  }

  return (
    <>
      {foldersAsTags.length ? (
        <ContentList
          variant='browse-guide'
          topics={foldersAsTags}
          emptyText={MESSAGE.EMPTY_TOPICS}
          baseRoute={match.url}
          onClick={item => trackComponentManualFolderClick(item.title)}
        />
      ) : null}

      {publicationsWithinTag.length ? (
        <ContentList
          variant='browse-guide'
          topics={publicationsWithinTag}
          emptyText={MESSAGE.EMPTY_TOPICS}
          baseRoute={match.url}
          {...pdfListProps}
        />
      ) : null}
    </>
  )
}

ComponentManualsContent.propTypes = {
  publications: PropTypes.array,
  match: PropTypes.object,
  baseRoute: PropTypes.string,
  currentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contents: PropTypes.object,
  vehicleInfo: PropTypes.object,
  offlineVehicleId: PropTypes.string,
  refreshOfflineCategories: PropTypes.func,
}

const ComponentManuals = props => (
  <BrowseGuideLayout match={props.match} baseRoute={props.baseRoute}>
    {({ refreshOfflineCategories, offlineVehicleId }) => (
      <ComponentManualsContent
        {...props}
        offlineVehicleId={offlineVehicleId}
        refreshOfflineCategories={refreshOfflineCategories}
      />
    )}
  </BrowseGuideLayout>
)

ComponentManuals.propTypes = {
  publications: PropTypes.array,
  match: PropTypes.object,
  baseRoute: PropTypes.string,
  currentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contents: PropTypes.object,
  vehicleInfo: PropTypes.object,
}

function mapStateToProps({ contents, vehicle }) {
  const processedContents = filteredContents(contents)
  const {
    currentId,
    currentBrand,
    currentYear,
    currentModel,
    currentProductType,
    currentProductSubtype,
    vehicles,
  } = vehicle

  let vehicleInfo = vehicles?.find(item => item.id === currentId) || null
  if (!vehicleInfo && currentId) {
    vehicleInfo = {
      id: currentId,
      make: currentBrand,
      year: currentYear,
      model: currentModel,
      FOREST_RIVER_PRODUCT_TYPE: currentProductType,
      FOREST_RIVER_PRODUCT_SUB_TYPE: currentProductSubtype,
    }
  }

  return {
    publications: processedContents.publications,
    currentId,
    contents,
    vehicleInfo,
  }
}

export default connect(mapStateToProps)(ComponentManuals)
